import React from 'react';
import { IPLabel, IPText } from 'baseComponents';
import { IPScrollablePane, IPScrollbarVisibility } from 'baseComponents/ScrollablePane/IPScrollablePane';
import { IPStack } from 'baseComponents/Stack/IPStack';
import { IPTextField } from 'baseComponents/TextField/IPTextField';
import RichTextEditor from 'components/Text/RichTextEditor';
import {
  globalStackItemStylesPaddingSceneScroll,
  globalStackTokensGapMedium,
  globalStackTokensGapSmall,
  globalTextFieldBig,
} from 'globalStyles';
import Task from 'models/tasks/task';
import User from 'models/user';
import UserPicker from 'components/Pickers/UserPicker';
import TimePicker from 'components/Pickers/TimePicker';
import { useTranslation } from 'react-i18next';
import LocalizedDatePicker from 'components/Pickers/LocalizedDatePicker';
import { addDateTimeDays, addDateTimeMinutes, getDateTimeDiffMinute } from 'utils/datetime';
import { globalTaskDefaultDeadline } from 'globalConstants';
import AppContext from 'App/AppContext';
import { TaskTagList } from 'components/Tasks/TaskTagList';
import Tag from 'models/tag';
import { TaskState } from 'models/tasks/taskHelperClasses';

interface IOutlookNormalTaskDetail {
  task: Task;
  orgTask: Task;
  getErrorMessage: (field: string) => string | undefined;
  onUpdate: (newTask: Task) => void;
  taskStates: TaskState[];
  isActionPending: boolean;
  loadRescheduleRange: () => void;
  tags: Tag[];
  addTagToTaskState: (tag: Tag) => void;
  removeTagFromTaskState: (tag: Tag) => void;
  onChangeCompletionDate: (completionDate: Date) => void;
}

export const OutlookNormalTaskDetail = (props: IOutlookNormalTaskDetail) => {
  const appContext = React.useContext(AppContext);
  const { t } = useTranslation(['task', 'control', 'translation', 'dateTimeComponent']);

  const setNewDateOnTask = (task: Task, newDate: Date) => {
    //trigger the load of the valid date range from the API so validation can be done
    props.loadRescheduleRange();
    task.setNewStartDate(newDate);
  };

  const setNewStartTimeOnTask = (task: Task, hours: number, mins: number) => {
    const duration = getDateTimeDiffMinute(props.task.endDateTime, props.task.startDateTime);

    //trigger the load of the valid date range from the API so validation can be done
    props.loadRescheduleRange();

    let newDate = new Date(props.task.startDateTime);
    newDate.setHours(hours);
    newDate.setMinutes(mins);
    newDate.setSeconds(0);
    newDate.setMilliseconds(0);

    task.startDateTime = newDate;
    task.endDateTime = addDateTimeMinutes(task.startDateTime, duration);
    task.recurringPattern.startDate = task.startDateTime;

    //when this is a new task, calculate a new default deadline
    if (task.taskId <= 0) {
      task.deadline = addDateTimeDays(task.startDateTime, globalTaskDefaultDeadline);
    }
  };

  const setNewEndTimeOnTask = (task: Task, hours: number, mins: number) => {
    //trigger the load of the valid date range from the API so validation can be done
    props.loadRescheduleRange();

    let newDate = new Date(props.task.startDateTime);
    newDate.setHours(hours);
    newDate.setMinutes(mins);
    newDate.setSeconds(0);
    newDate.setMilliseconds(0);

    task.endDateTime = newDate;

    //when this is a new task, calculate a new default deadline
    if (task.taskId <= 0) {
      task.deadline = addDateTimeDays(task.startDateTime, globalTaskDefaultDeadline);
    }
  };

  return (
    <IPStack verticalFill tokens={globalStackTokensGapSmall}>
      <IPStack.Item grow styles={globalStackItemStylesPaddingSceneScroll}>
        <IPScrollablePane scrollbarVisibility={IPScrollbarVisibility.auto}>
          <IPStack verticalFill tokens={globalStackTokensGapSmall}>
            <IPStack.Item>
              <IPTextField
                underlined
                autoFocus={!appContext.isMobileView}
                value={props.task.name ? props.task.name : ''}
                styles={globalTextFieldBig}
                onGetErrorMessage={(value) => {
                  if (value === '') return '';

                  return props.getErrorMessage('name');
                }}
                placeholder={t('task:Title.Placeholder')}
                onChange={(event: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>, newValue?: string) => {
                  let copyTask = props.task.clone();
                  copyTask.name = newValue ? newValue : '';
                  props.onUpdate(copyTask);
                }}
                maxLength={512}
              />
            </IPStack.Item>
            <IPStack.Item>
              <TaskTagList
                tags={props.tags}
                isLoading={props.isActionPending}
                addTagToTaskState={props.addTagToTaskState}
                removeTagFromTaskState={props.removeTagFromTaskState}
              />
            </IPStack.Item>
            <IPStack.Item>
              <RichTextEditor
                html={props.task.description}
                onChange={(value: string) => {
                  let copyTask = props.task.clone();
                  copyTask.description = value;
                  props.onUpdate(copyTask);
                }}
                placeholder={t('task:Description.Placeholder')}
                maxLength={8000}
                readOnly={false}
                maxHeight={300}
                directEdit={true}
              />
            </IPStack.Item>
            <IPStack.Item>
              <IPStack verticalFill>
                <IPLabel>{t('task:Assignment.Label')}</IPLabel>
                <UserPicker
                  users={[]}
                  selectedItemId={props.task.userId}
                  onSelect={(item: User | undefined) => {
                    let copyTask = props.task.clone();

                    copyTask.userId = undefined;
                    copyTask.user = undefined;

                    if (item) {
                      const user = item;
                      copyTask.userId = user.id;
                      copyTask.user = user;
                    }

                    props.onUpdate(copyTask);
                  }}
                />
                <IPText variant="small" styles={{ root: { color: 'red' } }}>
                  {props.getErrorMessage('assignment')}
                </IPText>
              </IPStack>
            </IPStack.Item>
            <IPStack.Item>
              <IPStack>
                <LocalizedDatePicker
                  label={t('task:Start.Label')}
                  showGoToToday
                  minDate={props.task.taskId < 0 ? new Date() : undefined}
                  value={props.task.startDateTime}
                  onDateChange={(newDate: Date | undefined) => {
                    if (newDate) {
                      let copyTask = props.task.clone();
                      setNewDateOnTask(copyTask, newDate);
                      props.onUpdate(copyTask);
                    }
                  }}
                />
                <IPText variant="small" styles={{ root: { color: 'red' } }}>
                  {props.getErrorMessage('endDateTime')}
                </IPText>
                <IPText variant="small" styles={{ root: { color: 'red' } }}>
                  {props.getErrorMessage('startDateTime')}
                </IPText>
              </IPStack>
            </IPStack.Item>
            <IPStack.Item>
              <IPStack horizontal tokens={globalStackTokensGapMedium}>
                <IPStack.Item>
                  <TimePicker
                    selectedTime={props.task.startDateTime}
                    label={t('task:Time.Label')}
                    onUpdateTime={(hours: number, mins: number) => {
                      let copyTask = props.task.clone();
                      setNewStartTimeOnTask(copyTask, hours, mins);
                      props.onUpdate(copyTask);
                    }}
                  />
                </IPStack.Item>
                <IPStack.Item>
                  <TimePicker
                    selectedTime={props.task.endDateTime}
                    label={t('task:End.Label')}
                    onUpdateTime={(hours: number, mins: number) => {
                      let copyTask = props.task.clone();
                      setNewEndTimeOnTask(copyTask, hours, mins);
                      props.onUpdate(copyTask);
                    }}
                  />
                </IPStack.Item>
              </IPStack>
            </IPStack.Item>
          </IPStack>
        </IPScrollablePane>
      </IPStack.Item>
    </IPStack>
  );
};
